/**
 * Meeting theme: **light is the default** when the user has not chosen anything (the OS preference
 * is deliberately ignored); the choice is persisted under `nimeet.meeting.theme`. A `?theme=dark`
 * / `?theme=light` URL parameter wins once and is then persisted, so screenshots/replays are
 * reproducible.
 *
 * The same `data-nk-theme` attribute drives the UI Kit components, whose CSS variables are
 * `:root` (light) + `[data-nk-theme='dark']` (dark) scoped — no second source of truth.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useI18n } from './i18n';

export type MeetingTheme = 'light' | 'dark';

export const THEME_STORAGE_KEY = 'nimeet.meeting.theme';

export function isMeetingTheme(value: unknown): value is MeetingTheme {
  return value === 'light' || value === 'dark';
}

function safeStorageRead(key: string): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeStorageWrite(key: string, value: string): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // best effort
  }
}

/** URL `?theme=` first, then the stored choice, then LIGHT — never the OS preference. */
export function initialTheme(search?: string): MeetingTheme {
  const query = search ?? (typeof window !== 'undefined' ? window.location.search : '');
  try {
    const fromUrl = new URLSearchParams(query).get('theme');
    if (isMeetingTheme(fromUrl)) return fromUrl;
  } catch {
    // ignore malformed query strings
  }
  const stored = safeStorageRead(THEME_STORAGE_KEY);
  return isMeetingTheme(stored) ? stored : 'light';
}

interface ThemeContextValue {
  theme: MeetingTheme;
  setTheme: (theme: MeetingTheme) => void;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function MeetingThemeProvider({
  children,
  initial,
}: {
  children: ReactNode;
  initial?: MeetingTheme;
}): ReactNode {
  const [theme, setThemeState] = useState<MeetingTheme>(() => initial ?? initialTheme());

  useEffect(() => {
    safeStorageWrite(THEME_STORAGE_KEY, theme);
    if (typeof document === 'undefined') return;
    const root = document.documentElement;
    // Both attributes: nm-* is the demo's own styling hook, nk-* is what @nimeet/ui-kit-react reads.
    root.dataset.nmTheme = theme;
    root.dataset.nkTheme = theme;
    root.style.colorScheme = theme;
  }, [theme]);

  const setTheme = useCallback((next: MeetingTheme) => {
    if (isMeetingTheme(next)) setThemeState(next);
  }, []);
  const toggleTheme = useCallback(
    () => setThemeState((current) => (current === 'dark' ? 'light' : 'dark')),
    [],
  );

  const value = useMemo<ThemeContextValue>(
    () => ({ theme, setTheme, toggleTheme }),
    [theme, setTheme, toggleTheme],
  );
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useMeetingTheme(): ThemeContextValue {
  const context = useContext(ThemeContext);
  if (!context) throw new Error('useMeetingTheme must be used inside <MeetingThemeProvider>');
  return context;
}

/** Sun/moon toggle with a localized accessible name. */
export function ThemeToggle({ className }: { className?: string }) {
  const { theme, toggleTheme } = useMeetingTheme();
  const { t } = useI18n();
  const label = theme === 'dark' ? t('theme.toLight') : t('theme.toDark');
  return (
    <button
      type="button"
      className={`nm-btn nm-theme-toggle${className ? ` ${className}` : ''}`}
      data-testid="theme-toggle"
      data-theme={theme}
      aria-label={label}
      title={label}
      onClick={toggleTheme}
    >
      <span aria-hidden="true">{theme === 'dark' ? '☀' : '☾'}</span>
      <span className="nm-theme-toggle__label">
        {theme === 'dark' ? t('theme.light') : t('theme.dark')}
      </span>
    </button>
  );
}
